<template>
  <div
    class="date-picker-container"
    :class="{ 'date-picker-container--open': showCalendar }"
  >
    <div
      class="date-picker-input-wrapper"
      :class="{
        'date-picker-input-wrapper--open': showCalendar,
        'date-picker-input-wrapper--error': showError,
      }"
    >
      <input
        :value="inputValue"
        type="text"
        :class="['date-picker-input', { 'date-picker-input--error': showError, 'date-picker-input--focused': showCalendar }]"
        :placeholder="placeholder"
        :readonly="readonly"
        @input="handleManualInput"
        @focus="!readonly && openCalendar()"
        @click="!readonly && openCalendar()"
        @blur="handleBlur"
      />
      <button
        v-if="!readonly"
        type="button"
        class="date-picker-calendar-button"
        :class="{ 'date-picker-calendar-button--active': showCalendar }"
        aria-label="Open calendar"
        @click.stop="toggleCalendar"
      >
        <i class="bi bi-calendar3" aria-hidden="true"></i>
      </button>
    </div>

    <Teleport to="body">
      <div
        v-if="showCalendar"
        ref="calendarEl"
        class="date-picker-calendar date-picker-calendar--teleported"
        :style="calendarStyle"
        @click.stop
        @mousedown.stop
      >
      <div class="date-picker-header">
        <button type="button" class="date-picker-nav-btn" @click="previousMonth">
          ‹
        </button>
        <div class="date-picker-month-year-container" @mousedown.stop @click.stop>
          <select
            v-model.number="selectedMonth"
            class="date-picker-month-select"
            aria-label="Select month"
            @mousedown.stop
            @click.stop
            @change="changeMonth"
          >
            <option v-for="(name, idx) in monthNames" :key="idx" :value="idx">
              {{ name }}
            </option>
          </select>
          <select
            v-model.number="selectedYear"
            class="date-picker-year-select"
            aria-label="Select year"
            @mousedown.stop
            @click.stop
            @change="changeYear"
          >
            <option v-for="year in availableYears" :key="year" :value="year">
              {{ year }}
            </option>
          </select>
        </div>
        <button type="button" class="date-picker-nav-btn" @click="nextMonth">
          ›
        </button>
      </div>
      
      <div class="date-picker-weekdays">
        <span v-for="day in weekdays" :key="day" class="date-picker-weekday">{{ day }}</span>
      </div>
      
      <div class="date-picker-days">
        <span 
          v-for="day in calendarDays" 
          :key="day.key"
          :class="[
            'date-picker-day',
            { 
              'other-month': day.otherMonth,
              'selected': day.selected,
              'today': day.today
            }
          ]"
          @click="selectDate(day)"
        >
          {{ day.day }}
        </span>
      </div>
      
      <div class="date-picker-footer">
        <button type="button" class="date-picker-today-btn" @click="selectToday">
          Today
        </button>
        <button type="button" class="date-picker-clear-btn" @click="clearDate">
          Clear
        </button>
      </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import './DatePicker.css';

export default {
  name: 'DatePicker',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'MM/DD/YYYY'
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showCalendar: false,
      currentDate: new Date(),
      selectedDate: null,
      showError: false,
      selectedYear: new Date().getFullYear(),
      selectedMonth: new Date().getMonth(),
      manualInputValue: '',
      calendarStyle: {
        top: '0px',
        left: '0px',
        width: '280px',
      },
    }
  },
  computed: {
    displayValue() {
      if (this.selectedDate) {
        const month = String(this.selectedDate.getMonth() + 1).padStart(2, '0');
        const day = String(this.selectedDate.getDate()).padStart(2, '0');
        const year = this.selectedDate.getFullYear();
        return `${month}/${day}/${year}`;
      }
      // Return empty string if no selectedDate and modelValue is empty/null
      if (!this.modelValue || this.modelValue.trim() === '') {
        return '';
      }
      return this.modelValue;
    },
    inputValue() {
      if (this.manualInputValue) {
        return this.manualInputValue;
      }
      if (this.selectedDate) {
        const month = String(this.selectedDate.getMonth() + 1).padStart(2, '0');
        const day = String(this.selectedDate.getDate()).padStart(2, '0');
        const year = this.selectedDate.getFullYear();
        return `${month}/${day}/${year}`;
      }
      // Return empty string if no selectedDate and modelValue is empty/null
      if (!this.modelValue || this.modelValue.trim() === '') {
        return '';
      }
      return this.modelValue;
    },
    monthNames() {
      return [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
    },
    weekdays() {
      return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    },
    calendarDays() {
      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const startDate = new Date(firstDay);
      startDate.setDate(startDate.getDate() - firstDay.getDay());
      
      const days = [];
      const today = new Date();
      
      for (let i = 0; i < 42; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        
        const isOtherMonth = date.getMonth() !== month;
        const isSelected = this.selectedDate && 
          date.getDate() === this.selectedDate.getDate() &&
          date.getMonth() === this.selectedDate.getMonth() &&
          date.getFullYear() === this.selectedDate.getFullYear();
        const isToday = date.getDate() === today.getDate() &&
          date.getMonth() === today.getMonth() &&
          date.getFullYear() === today.getFullYear();
        
        days.push({
          key: i,
          day: date.getDate(),
          date: date,
          otherMonth: isOtherMonth,
          selected: isSelected,
          today: isToday
        });
      }
      
      return days;
    },
    availableYears() {
      const currentYear = new Date().getFullYear();
      const startYear = currentYear - 50;
      const endYear = currentYear + 20;
      const years = [];
      for (let i = startYear; i <= endYear; i++) {
        years.push(i);
      }
      return years;
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newValue) {
        if (newValue && newValue.trim()) {
          this.parseDate(newValue);
        } else {
          this.selectedDate = null;
        }
      }
    },
    currentDate: {
      handler() {
        this.selectedYear = this.currentDate.getFullYear();
        this.selectedMonth = this.currentDate.getMonth();
      },
      immediate: true
    },
    showCalendar(isOpen) {
      if (isOpen) {
        this.$nextTick(() => {
          this.positionCalendar();
          requestAnimationFrame(() => {
            this.positionCalendar();
            requestAnimationFrame(() => this.positionCalendar());
          });
        });
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
    window.addEventListener('resize', this.positionCalendar);
    window.addEventListener('scroll', this.positionCalendar, true);
    this.selectedYear = this.currentDate.getFullYear();
    this.selectedMonth = this.currentDate.getMonth();
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
    window.removeEventListener('resize', this.positionCalendar);
    window.removeEventListener('scroll', this.positionCalendar, true);
  },
  methods: {
    positionCalendar() {
      if (!this.showCalendar || !this.$el) return;

      const wrapper = this.$el.querySelector('.date-picker-input-wrapper');
      if (!wrapper) return;

      const rect = wrapper.getBoundingClientRect();
      const calendarEl = this.$refs.calendarEl;
      const calendarWidth = Math.max(280, Math.round(rect.width));
      const calendarHeight = calendarEl?.offsetHeight || 340;
      const gap = 4;
      const viewportPadding = 8;

      let top = rect.bottom + gap;
      let left = rect.left;

      if (left + calendarWidth > window.innerWidth - viewportPadding) {
        left = window.innerWidth - calendarWidth - viewportPadding;
      }
      if (left < viewportPadding) {
        left = viewportPadding;
      }

      const spaceBelow = window.innerHeight - rect.bottom - gap;
      const spaceAbove = rect.top - gap;
      if (spaceBelow < calendarHeight && spaceAbove > spaceBelow) {
        top = Math.max(viewportPadding, rect.top - calendarHeight - gap);
      }

      top = Math.min(
        Math.max(viewportPadding, top),
        window.innerHeight - calendarHeight - viewportPadding
      );

      this.calendarStyle = {
        top: `${Math.round(top)}px`,
        left: `${Math.round(left)}px`,
        width: `${calendarWidth}px`,
        zIndex: 11150,
      };
    },

    openCalendar() {
      if (this.readonly) return;
      this.showCalendar = true;
    },

    closeCalendar() {
      this.showCalendar = false;
    },
    parseDate(dateString) {
      if (!dateString || !dateString.trim()) {
        this.selectedDate = null;
        this.showError = false;
        return;
      }

      const trimmed = dateString.trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        const [y, m, d] = trimmed.split('-').map((x) => parseInt(x, 10));
        if (!Number.isNaN(y) && !Number.isNaN(m) && !Number.isNaN(d) && m >= 1 && m <= 12 && d >= 1 && d <= 31) {
          const date = new Date(y, m - 1, d);
          if (date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d) {
            this.selectedDate = date;
            this.currentDate = new Date(date);
            this.selectedYear = y;
            this.selectedMonth = date.getMonth();
            this.showError = false;
            return;
          }
        }
      }
      
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const month = parseInt(parts[0], 10) - 1;
        const day = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        
        if (!isNaN(month) && !isNaN(day) && !isNaN(year) && 
            month >= 0 && month <= 11 && 
            day >= 1 && day <= 31 && 
            year >= 1900 && year <= 2100) {
          const date = new Date(year, month, day);
          if (date.getFullYear() === year && 
              date.getMonth() === month && 
              date.getDate() === day) {
            this.selectedDate = date;
            this.currentDate = new Date(date);
            this.selectedYear = year;
            this.selectedMonth = month;
            this.showError = false;
            return;
          }
        }
      }
      
      // If we get here, the date is invalid
      this.showError = true;
      this.selectedDate = null;
    },
    
    selectDate(day) {
      if (day.otherMonth) return;
      
      this.selectedDate = day.date;
      this.currentDate = new Date(day.date);
      this.manualInputValue = '';
      this.showError = false;
      this.emitValue();
      this.closeCalendar();
    },
    
    selectToday() {
      const today = new Date();
      this.selectedDate = today;
      this.currentDate = new Date(today);
      this.manualInputValue = '';
      this.showError = false;
      this.emitValue();
      this.closeCalendar();
    },
    
    clearDate() {
      this.selectedDate = null;
      this.manualInputValue = '';
      this.showError = false;
      this.$emit('update:modelValue', '');
      this.$emit('change', '');
      this.closeCalendar();
    },
    
    previousMonth() {
      this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() - 1, 1);
    },
    
    nextMonth() {
      this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 1);
    },
    
    toggleCalendar() {
      if (this.readonly) return;
      if (this.showCalendar) {
        this.closeCalendar();
      } else {
        this.openCalendar();
      }
    },
    
    handleOutsideClick(event) {
      const calendarEl = this.$refs.calendarEl;
      const insidePicker = this.$el.contains(event.target)
        || (calendarEl && calendarEl.contains(event.target));
      if (!insidePicker) {
        this.closeCalendar();
      }
    },
    
    emitValue() {
      const value = this.displayValue;
      this.$emit('update:modelValue', value);
      this.$emit('change', value);
    },

    changeYear() {
      const currentDay = this.currentDate.getDate();
      const lastDay = new Date(this.selectedYear, this.selectedMonth + 1, 0).getDate();
      const day = Math.min(currentDay, lastDay);
      this.currentDate = new Date(this.selectedYear, this.selectedMonth, day);
    },

    changeMonth() {
      const currentDay = this.currentDate.getDate();
      const lastDay = new Date(this.selectedYear, this.selectedMonth + 1, 0).getDate();
      const day = Math.min(currentDay, lastDay);
      this.currentDate = new Date(this.selectedYear, this.selectedMonth, day);
    },

    handleManualInput(event) {
      if (this.readonly) return;
      
      let value = event.target.value;
      
      // Remove all non-digit characters
      const digitsOnly = value.replace(/\D/g, '');
      
      // Format as MM/DD/YYYY as user types
      if (digitsOnly.length <= 2) {
        value = digitsOnly;
      } else if (digitsOnly.length <= 4) {
        value = digitsOnly.slice(0, 2) + '/' + digitsOnly.slice(2);
      } else if (digitsOnly.length <= 8) {
        value = digitsOnly.slice(0, 2) + '/' + digitsOnly.slice(2, 4) + '/' + digitsOnly.slice(4);
      } else {
        value = digitsOnly.slice(0, 2) + '/' + digitsOnly.slice(2, 4) + '/' + digitsOnly.slice(4, 8);
      }
      
      // Update the manual input value
      this.manualInputValue = value;
      
      // Parse the date and update the component state
      this.parseDate(value);
      
      // Emit the value if it's valid
      if (this.selectedDate && !this.showError) {
        this.emitValue();
        this.manualInputValue = ''; // Clear manual input after successful parse
      }
    },

    handleBlur() {
      // Teleported calendar uses mousedown.prevent; short delay for outside tab focus
      setTimeout(() => {
        const active = document.activeElement;
        const calendarEl = this.$refs.calendarEl;
        if (calendarEl && active && calendarEl.contains(active)) {
          return;
        }
        this.closeCalendar();
      }, 150);
      
      // Validate the current input value
      const inputValue = this.manualInputValue || this.displayValue;
      if (inputValue && inputValue.trim()) {
        this.parseDate(inputValue);
        if (!this.selectedDate) {
          this.showError = true;
        } else {
          this.showError = false;
          this.manualInputValue = ''; // Clear manual input after successful validation
        }
      } else {
        this.showError = false;
        this.manualInputValue = ''; // Clear manual input if empty
      }
    }
  }
}
</script> 